import { useMemo, useState } from "react"
import { Check, X } from "lucide-react"

import type { Attachment } from "components/mentorship/EventsListCard"
import { Icon } from "components/icons/Icon"
import { AppDialog } from "components/ui/app-dialog"
import { Button } from "components/ui/button"
import { Input } from "components/ui/input"
import { Textarea } from "components/ui/textarea"
import { messages } from "services/api"
import type { CohortRole } from "types"
import type { CohortRoleLabelSource } from "utils/cohortRoleLabels"
import { getInviteeBucketLabel } from "utils/cohortRoleLabels"

type MessageRecipient = "individual" | "mentors" | "cohort" | "scholars"
type MessageChannels = "email" | "text" | "email-text"
type MessageResult = {
  tone: "success" | "partial" | "failure"
  title: string
  detail: string
  failedChannels: Array<"email" | "sms">
  channelResults: Array<{
    channel: "Email" | "Text"
    detail: string
    tone: "success" | "partial" | "failure"
  }>
}
type RosterPerson = {
  id: string | number
  name: string
  role?: string
  roleKey?: CohortRole
  smsCapable?: boolean
  smsUnavailableReason?: "missing_phone" | "opted_out" | null
}

type MessagesCardProps = {
  recipient: MessageRecipient
  onRecipientChange: (recipient: MessageRecipient) => void
  subject: string
  onSubjectChange: (value: string) => void
  messageBody: string
  onMessageBodyChange: (value: string) => void
  channel: MessageChannels
  onChannelChange: (channel: MessageChannels) => void
  attachments: Attachment[]
  onAttachmentsChange: (attachments: Attachment[]) => void
  roster?: RosterPerson[]
  currentUserId?: string | number
  selectedIndividuals: string[]
  onSelectedIndividualsChange: (ids: string[]) => void
  roleLabels?: CohortRoleLabelSource
  className?: string
}

const smsReasonLabel = (reason?: "missing_phone" | "opted_out" | null) => {
  if (reason === "missing_phone") return "No phone number"
  if (reason === "opted_out") return "Opted out of texts"
  return null
}

const rosterPersonHasRole = (user: RosterPerson, roleKey: "mentor" | "mentee") => {
  if (user.roleKey === roleKey) return true
  const role = (user.role || "").toLowerCase()
  return roleKey === "mentor" ? role.includes("mentor") : role.includes("scholar") || role.includes("mentee")
}

export function MessagesCard({
  recipient,
  onRecipientChange,
  subject,
  onSubjectChange,
  messageBody,
  onMessageBodyChange,
  channel,
  onChannelChange,
  roster = [],
  currentUserId,
  selectedIndividuals,
  onSelectedIndividualsChange,
  roleLabels,
  className,
}: MessagesCardProps) {
  const [composerOpen, setComposerOpen] = useState(false)
  const [individualSearch, setIndividualSearch] = useState("")
  const [sending, setSending] = useState(false)
  const [validationError, setValidationError] = useState("")
  const [confirming, setConfirming] = useState(false)
  const [discardConfirm, setDiscardConfirm] = useState(false)
  const [result, setResult] = useState<MessageResult | null>(null)

  const rosterOptions = useMemo(
    () => (currentUserId ? roster.filter((user) => String(user.id) !== String(currentUserId)) : roster),
    [currentUserId, roster],
  )

  const filteredRosterOptions = useMemo(() => {
    const term = individualSearch.trim().toLowerCase()
    const selected = new Set(selectedIndividuals.map(String))
    return rosterOptions.filter((user) => !selected.has(String(user.id)) && (!term || user.name.toLowerCase().includes(term)))
  }, [individualSearch, rosterOptions, selectedIndividuals])

  const recipients = useMemo(() => {
    if (recipient === "individual") {
      const ids = new Set(selectedIndividuals.map(String))
      return rosterOptions.filter((user) => ids.has(String(user.id)))
    }
    if (recipient === "mentors") return rosterOptions.filter((user) => rosterPersonHasRole(user, "mentor"))
    if (recipient === "scholars") return rosterOptions.filter((user) => rosterPersonHasRole(user, "mentee"))
    return rosterOptions
  }, [recipient, rosterOptions, selectedIndividuals])

  const includesText = channel === "text" || channel === "email-text"
  const includesEmail = channel === "email" || channel === "email-text"
  const smsUnavailable = includesText ? recipients.filter((user) => !user.smsCapable || user.smsUnavailableReason) : []
  const textReachableCount = Math.max(0, recipients.length - smsUnavailable.length)
  const channelLabel = channel === "email" ? "an email" : channel === "text" ? "a text" : "an email and a text"

  const openComposer = (nextRecipient?: MessageRecipient) => {
    if (nextRecipient) onRecipientChange(nextRecipient)
    setValidationError("")
    setResult(null)
    setConfirming(false)
    setDiscardConfirm(false)
    setComposerOpen(true)
  }

  const requestClose = () => {
    if (sending) return
    if (result) {
      if (result.tone !== "failure") {
        onMessageBodyChange("")
        onSubjectChange("")
        onSelectedIndividualsChange([])
      }
      setComposerOpen(false)
      setResult(null)
      setConfirming(false)
      return
    }
    if (!result && messageBody.trim().length >= 20 && !discardConfirm) {
      setDiscardConfirm(true)
      setConfirming(false)
      return
    }
    setComposerOpen(false)
    setDiscardConfirm(false)
    setConfirming(false)
    setResult(null)
  }

  const sendSingleChannel = async (singleChannel: "email" | "sms", userIds: string[]) => messages.sendAdminMessage({
    channel: singleChannel,
    recipient_type: "custom_list",
    user_ids: userIds,
    subject: singleChannel === "email" ? subject : undefined,
    body: messageBody,
  })

  const sendMessage = async (retryChannels?: Array<"email" | "sms">) => {
    const userIds = recipients.map((person) => String(person.id))
    setValidationError("")
    if (!messageBody.trim()) {
      setValidationError("Write a message before sending.")
      setConfirming(false)
      return
    }
    if (!userIds.length || (channel === "text" && textReachableCount === 0)) {
      setValidationError("Nobody in this selection can be reached. Choose someone else.")
      setConfirming(false)
      return
    }

    const channels: Array<"email" | "sms"> = retryChannels || (channel === "email" ? ["email"] : channel === "text" ? ["sms"] : ["email", "sms"])
    setSending(true)
    setResult(null)
    try {
      const successes: Array<{ channel: "email" | "sms"; sent: number; skipped: number; failed: number }> = []
      const failures: Array<"email" | "sms"> = []
      for (const singleChannel of channels) {
        try {
          const response = await sendSingleChannel(singleChannel, userIds)
          successes.push({ channel: singleChannel, sent: response.sent_count, skipped: response.skipped_count, failed: response.failed_count })
          if (response.failed_count > 0) failures.push(singleChannel)
        } catch {
          failures.push(singleChannel)
        }
      }

      const email = successes.find((item) => item.channel === "email")
      const text = successes.find((item) => item.channel === "sms")
      const channelResults: MessageResult["channelResults"] = channels.map((singleChannel) => {
        const outcome = successes.find((item) => item.channel === singleChannel)
        const label = singleChannel === "email" ? "Email" : "Text"
        if (!outcome) return { channel: label, detail: "Not sent", tone: "failure" }
        const skippedDetail = outcome.skipped ? ` · ${outcome.skipped} skipped` : ""
        const failedDetail = outcome.failed ? ` · ${outcome.failed} failed` : ""
        return {
          channel: label,
          detail: `${outcome.sent} sent${skippedDetail}${failedDetail}`,
          tone: outcome.failed ? "partial" : "success",
        }
      })
      if (!successes.length) {
        setResult({ tone: "failure", title: "We couldn't send this message.", detail: "Nobody received anything, and your message is still here.", failedChannels: failures, channelResults })
      } else if (failures.length) {
        const title = email && !text ? "The email went out. The texts didn't." : text && !email ? "The texts went out. The email didn't." : "Some messages couldn't be sent."
        setResult({ tone: "partial", title, detail: "Retrying sends only the failed channel. Nobody gets a duplicate.", failedChannels: failures, channelResults })
      } else {
        const detail = [email ? `${email.sent} emails` : null, text ? `${text.sent} texts` : null].filter(Boolean).join(" and ")
        setResult({ tone: "success", title: `Sent to ${recipients.length} ${recipients.length === 1 ? "person" : "people"}.`, detail: `${detail} sent.${smsUnavailable.length ? ` ${smsUnavailable.length} skipped for text.` : ""}`, failedChannels: [], channelResults })
      }
      setConfirming(false)
    } finally {
      setSending(false)
    }
  }

  const reviewSend = () => {
    setValidationError("")
    if (!messageBody.trim()) {
      setValidationError("Write a message before sending.")
      return
    }
    if (!recipients.length || (channel === "text" && textReachableCount === 0)) {
      setValidationError("Nobody in this selection can be reached. Choose someone else.")
      return
    }
    if (recipient === "individual" && recipients.length === 1) {
      void sendMessage()
      return
    }
    setConfirming(true)
  }

  return (
    <section className={`cohort-open-section min-w-0 ${className || ""}`} id="admin-messages" aria-labelledby="admin-messages-heading">
      <div className="cohort-messages-panel rounded-[14px] p-5 text-[var(--cohort-hero-ink)]">
        <h2 id="admin-messages-heading" className="text-xl text-[var(--cohort-hero-ink)]">Messages</h2>
        <p className="mt-1 text-sm leading-5 text-[var(--cohort-hero-muted)]">Send an email, a text, or both to people in this cohort.</p>
        <Button type="button" onClick={() => openComposer()} className="cohort-hero-button mt-4 min-h-[46px] w-full rounded-xl bg-[var(--cohort-hero-action)] text-[var(--cohort-hero-action-ink)] shadow-none hover:bg-white active:bg-[var(--cohort-hero-action-press)]">Compose message</Button>
      </div>

      {composerOpen ? (
        <AppDialog
          onClose={requestClose}
          closeOnEsc={!discardConfirm}
          closeOnOutside={!discardConfirm}
          hideCloseButton={discardConfirm}
          onEscapeKeyDown={() => {
            if (discardConfirm) setDiscardConfirm(false)
          }}
          isBusy={sending}
          size="md"
          className="cohort-dialog cohort-dialog--framed max-h-[calc(100dvh-2rem)] max-w-[660px]"
          title={discardConfirm ? "Keep this draft?" : "Compose message"}
          titleClassName="pr-10 text-[25px] font-normal"
          description={discardConfirm ? "You've written something you haven't sent." : "Choose who should receive this and how it should reach them."}
          bodyClassName="space-y-5"
          actions={
            <div className="w-full">
              {!discardConfirm ? <p className="mb-3 text-sm text-[var(--cohort-ink-muted)]">Sending {channelLabel} to <strong className="text-[var(--cohort-ink)]">{recipients.length} {recipients.length === 1 ? "person" : "people"}</strong>. {includesText && smsUnavailable.length ? `${smsUnavailable.length} will ${includesEmail ? "only get the email" : "not get the text"}.` : ""}</p> : null}
              <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                {discardConfirm ? <><Button type="button" onClick={() => setDiscardConfirm(false)} disabled={sending} className="cohort-primary-button">Keep writing</Button><Button type="button" variant="secondary" disabled={sending} onClick={() => { onMessageBodyChange(""); onSubjectChange(""); onSelectedIndividualsChange([]); setComposerOpen(false); setDiscardConfirm(false) }} className="cohort-secondary-button">Discard</Button></> : result ? <>{result.failedChannels.length ? <Button type="button" onClick={() => void sendMessage(result.failedChannels)} disabled={sending} className="cohort-primary-button">{sending ? "Sending…" : `Retry the ${result.failedChannels[0] === "sms" ? "texts" : "email"}`}</Button> : null}<Button type="button" variant="secondary" onClick={requestClose} disabled={sending} className="cohort-secondary-button">Close</Button></> : confirming ? <><Button type="button" variant="secondary" onClick={() => setConfirming(false)} disabled={sending} className="cohort-secondary-button">Back to editing</Button><Button type="button" onClick={() => void sendMessage()} disabled={sending} className="cohort-primary-button">{sending ? "Sending…" : `Send to ${recipients.length} ${recipients.length === 1 ? "person" : "people"}`}</Button></> : <><Button type="button" variant="secondary" onClick={requestClose} disabled={sending} className="cohort-secondary-button">Cancel</Button><Button type="button" onClick={reviewSend} disabled={sending} className="cohort-primary-button">Review and send</Button></>}
              </div>
            </div>
          }
        >
          {discardConfirm ? null : (
            <>
              <div>
                <p className="mb-2 text-sm font-medium">How to send it</p>
                <div className="grid grid-cols-3 gap-2" role="group" aria-label="Message channel">
                  {(["email", "text", "email-text"] as const).map((value) => <button key={value} type="button" aria-pressed={channel === value} disabled={sending} onClick={() => onChannelChange(value)} className="cohort-choice flex min-h-[46px] items-center justify-center gap-2 px-2 text-[15px] disabled:cursor-not-allowed disabled:opacity-60">{channel === value ? <Check className="h-4 w-4" aria-hidden="true" /> : null}{value === "email-text" ? <><span className="sm:hidden">Both</span><span className="hidden sm:inline">Email and text</span></> : value === "text" ? "Text" : "Email"}</button>)}
                </div>
              </div>
              <div>
                <p className="mb-2 text-sm font-medium">Who it goes to</p>
                <div className="flex flex-wrap gap-2" role="group" aria-label="Message audience">
                  {([
                    ["cohort", `Everyone · ${rosterOptions.length}`],
                    ["scholars", `${getInviteeBucketLabel("scholars", roleLabels)} · ${rosterOptions.filter((user) => rosterPersonHasRole(user, "mentee")).length}`],
                    ["mentors", `${getInviteeBucketLabel("mentors", roleLabels)} · ${rosterOptions.filter((user) => rosterPersonHasRole(user, "mentor")).length}`],
                    ["individual", "Pick people"],
                  ] as const).map(([value, label]) => <button key={value} type="button" aria-pressed={recipient === value} disabled={sending} onClick={() => onRecipientChange(value)} className="cohort-choice flex min-h-[46px] items-center gap-2 px-4 text-[15px] disabled:cursor-not-allowed disabled:opacity-60">{recipient === value ? <Check className="h-4 w-4" aria-hidden="true" /> : null}{label}</button>)}
                </div>
                {recipient === "individual" ? (
                  <div className="mt-3 space-y-2">
                    <Input value={individualSearch} onChange={(event) => setIndividualSearch(event.target.value)} disabled={sending} placeholder="Search by name" className="min-h-11 rounded-xl" />
                    {individualSearch && filteredRosterOptions.length ? <div className="cohort-bounded-list cohort-bounded-list--compact max-h-40 rounded-xl border border-[var(--cohort-rule)] bg-white" role="region" aria-label="Matching message recipients">{filteredRosterOptions.map((user) => <button key={String(user.id)} type="button" disabled={sending} onClick={() => { onSelectedIndividualsChange([...selectedIndividuals, String(user.id)]); setIndividualSearch("") }} className="cohort-row-action flex min-h-11 w-full items-center justify-between gap-3 border-b border-[var(--cohort-rule-soft)] px-3 text-left text-sm last:border-0 disabled:cursor-not-allowed disabled:opacity-60"><span>{user.name}</span><span className="text-xs text-[var(--cohort-ink-muted)]">{user.role}</span></button>)}</div> : null}
                    <div className="cohort-bounded-list cohort-bounded-list--compact flex flex-wrap gap-2" role="region" aria-label="Selected message recipients">{recipients.map((user) => <span key={String(user.id)} className="inline-flex items-center gap-2 rounded-xl border border-[var(--cohort-rule)] bg-white py-1 pl-3 pr-1 text-sm">{user.name}<button type="button" disabled={sending} onClick={() => onSelectedIndividualsChange(selectedIndividuals.filter((id) => id !== String(user.id)))} className="cohort-icon-button flex h-11 w-11 items-center justify-center disabled:cursor-not-allowed disabled:opacity-60" aria-label={`Remove ${user.name}`}><X className="h-3.5 w-3.5" /></button></span>)}</div>
                  </div>
                ) : null}
              </div>
              {includesText && smsUnavailable.length ? (
                <div>
                  <h3 className="text-base">Who can&apos;t get the text</h3>
                  <div className="cohort-bounded-list cohort-bounded-list--compact mt-2" role="region" aria-label="People who cannot receive text messages" tabIndex={0}>{smsUnavailable.map((person) => <div key={String(person.id)} className="cohort-ruled-row flex min-h-11 items-center justify-between gap-3 text-sm"><span>{person.name}</span><span className="text-[var(--cohort-ochre)]">{smsReasonLabel(person.smsUnavailableReason) || "No phone number"}</span></div>)}</div>
                  {includesEmail ? <p className="mt-2 text-sm text-[var(--cohort-ink-muted)]">These {smsUnavailable.length} still get the email.</p> : null}
                </div>
              ) : null}
              {includesEmail ? <div><label htmlFor="message-subject" className="mb-2 block text-sm font-medium">Subject <span className="font-normal text-[var(--cohort-ink-muted)]">— email only</span></label><Input id="message-subject" value={subject} onChange={(event) => onSubjectChange(event.target.value)} disabled={sending} className="min-h-11 rounded-xl bg-white" /></div> : null}
              <div><label htmlFor="message-body" className="mb-2 block text-sm font-medium">Message</label><Textarea id="message-body" value={messageBody} onChange={(event) => onMessageBodyChange(event.target.value)} disabled={sending} className="min-h-40 rounded-xl bg-white" /></div>
              <div className="flex items-start gap-2.5 rounded-xl border border-[var(--cohort-ochre-rule)] bg-[var(--cohort-ochre-soft)] p-3 text-sm leading-5 text-[var(--cohort-ochre)]"><Icon name="caution" className="mt-0.5 h-4 w-4 shrink-0" /><span>Attachments can&apos;t be sent yet. Link to the file in your message instead.</span></div>
              {confirming ? <div className="rounded-xl border border-[var(--cohort-rule)] bg-white p-4"><h3 className="text-lg">Send this to {recipient === "cohort" ? "everyone in this cohort" : `${recipients.length} people`}?</h3><p className="mt-2 text-sm text-[var(--cohort-ink-muted)]">There&apos;s no way to unsend it.</p></div> : null}
              {validationError ? <p className="text-sm text-[var(--cohort-clay)]">{validationError}</p> : null}
              {result ? <div className={`rounded-xl border p-4 ${result.tone === "success" ? "border-[var(--cohort-success)] bg-[var(--cohort-success-soft)]" : result.tone === "partial" ? "border-[var(--cohort-ochre-rule)] bg-[var(--cohort-ochre-soft)]" : "border-[var(--cohort-clay-rule)] bg-[var(--cohort-clay-soft)]"}`}><h3 className="text-lg">{result.title}</h3><p className="mt-2 text-sm text-[var(--cohort-ink-muted)]">{result.detail}</p><div className="mt-3 divide-y divide-[var(--cohort-rule)] border-y border-[var(--cohort-rule)]">{result.channelResults.map((channelResult) => <div key={channelResult.channel} className="flex items-center justify-between gap-4 py-2 text-sm"><span className="font-medium text-[var(--cohort-ink)]">{channelResult.channel}</span><span className={channelResult.tone === "failure" ? "text-[var(--cohort-clay)]" : channelResult.tone === "partial" ? "text-[var(--cohort-ochre)]" : "text-[var(--cohort-ink-muted)]"}>{channelResult.detail}</span></div>)}</div></div> : null}
            </>
          )}
        </AppDialog>
      ) : null}
    </section>
  )
}
